import React, { useMemo, useEffect, useRef } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  LineSeries,
  Category,
  Logarithmic,
  Tooltip,
  Inject,
  Legend,
} from "@syncfusion/ej2-react-charts";
import { formatNumber } from "../../utils/formatNumber";
import "./Chart.css";

const DEFAULT_PALETTE = [
  "#eb4132",
  "#fcbd01",
  "#31aa52",
  "#1976d2",
  "#9c27b0",
  "#ff6f00",
  "#00acc1",
  "#6d4c41",
  "#d81b60",
  "#3949ab",
  "#7cb342",
  "#00897b",
];

function seriesColor(i) {
  return DEFAULT_PALETTE[i % DEFAULT_PALETTE.length];
}

function xIntervalForSpan(min, max) {
  const span = Math.max(Number(max) - Number(min), 1e-6);
  if (span <= 2) return 0.25;
  if (span <= 6) return 1;
  // Match original full-day chart (0–24): 2h ticks through 24:00.
  return 2;
}

export default function MultiLineAvgChart({
  title = "Average Active TV",
  series = [],
  refreshKey,
  yAxisScale = "linear",
  yName = "active",
  logBase = 10,
  xAxisMinimum,
  xAxisMaximum,
  emphasizedChannelId,
  channelColorById = {},
}) {
  const chartRef = useRef(null);

  const xMin =
    typeof xAxisMinimum === "number" && Number.isFinite(xAxisMinimum)
      ? xAxisMinimum
      : 0;
  const xMax =
    typeof xAxisMaximum === "number" && Number.isFinite(xAxisMaximum)
      ? xAxisMaximum
      : 24;

  const xAxis = useMemo(
    () => ({
      valueType: "Double",
      minimum: xMin,
      maximum: Math.max(xMax, xMin + 1 / 60),
      interval: xIntervalForSpan(xMin, xMax),
      rangePadding: "None",
    }),
    [xMin, xMax]
  );

  useEffect(() => {
    chartRef.current?.refresh();
  }, [refreshKey, xMin, xMax, emphasizedChannelId]);

  const safeSeries = useMemo(
    () => (Array.isArray(series) ? series : []),
    [series]
  );

  const useLog = yAxisScale === "log";

  const plottedSeries = useMemo(() => {
    return safeSeries.map((s) => ({
      ...s,
      data: (s.data || []).map((p) => {
        const raw = Number(p?.active);
        const rawSafe = Number.isFinite(raw) ? Math.max(0, raw) : 0;

        if (useLog) {
          return {
            ...p,
            activePlot: rawSafe <= 0 ? 1 : rawSafe,
          };
        }

        return {
          ...p,
          active: rawSafe,
        };
      }),
    }));
  }, [safeSeries, useLog]);

  const emphasisRequested =
    emphasizedChannelId != null && String(emphasizedChannelId).trim() !== "";

  const emphasisMatchesSeries =
    emphasisRequested &&
    plottedSeries.some(
      (s) =>
        String(s.channelId ?? "").trim() ===
        String(emphasizedChannelId).trim()
    );

  const emphasisActive = emphasisRequested && emphasisMatchesSeries;

  const seriesForRender = useMemo(() => {
    const decorated = plottedSeries.map((s, i) => {
      const channelHex = channelColorById[s.channelId];
      const color = channelHex || seriesColor(i);
      const sid =
        s.channelId != null ? String(s.channelId).trim() : "";
      const focus =
        emphasisActive && sid === String(emphasizedChannelId).trim();
      const dim = emphasisActive && !focus;
      return {
        series: s,
        color,
        lineWidth: focus ? 4 : dim ? 2 : 3,
        lineOpacity: dim ? 0.18 : 1,
        drawOnTop: focus ? 1 : 0,
      };
    });
    return decorated.sort((a, b) => a.drawOnTop - b.drawOnTop);
  }, [plottedSeries, emphasisActive, emphasizedChannelId, channelColorById]);

  // ✅ TOOLTIP FIX
  const handleTooltipRender = (args) => {
    const x = args?.point?.x;
    const y = args?.point?.y ?? args?.point?.active;

    if (typeof x === "number") {
      const hour = Math.floor(x);
      const min = Math.round((x - hour) * 60);

      const time =
        String(hour).padStart(2, "0") +
        ":" +
        String(min).padStart(2, "0");

      args.text = `${time} : ${formatNumber(y ?? 0)}`;
    }
  };

  const handleAxisLabelRender = (args) => {
    if (args.axis?.name === "primaryXAxis") {
      const val = Number(args.value);
      if (Math.abs(val - 24) < 0.02) {
        args.text = "23:59";
        return;
      }
      const hour = Math.floor(val);
      const min = Math.round((val - hour) * 60);

      args.text =
        String(hour).padStart(2, "0") +
        ":" +
        String(min).padStart(2, "0");
    }

    if (args.axis?.name === "primaryYAxis") {
      const val = Number(args.value);

      if (!isNaN(val)) {
        if (useLog) {
          const isPower = Number.isInteger(val);

          if (isPower) {
            const realValue = Math.pow(logBase, val);
            args.text = formatNumber(realValue);
          } else {
            args.text = "";
          }
        } else {
          args.text = formatNumber(val);
        }
      }
    }
  };

  return (
    <div className="chart-container">
      <h3 className="chart-title">{title}</h3>

      <ChartComponent
        ref={chartRef}
        primaryXAxis={xAxis}
        margin={{ right: 40, left: 15 }} 
        primaryYAxis={{
          minimum: useLog ? 1 : 0,
          valueType: useLog ? "Logarithmic" : "Double",
          logBase: useLog ? logBase : undefined,
          interval: useLog ? 1 : undefined,
        }}
          legendSettings={{
            visible: true,
            position: "Top",
            alignment: "Center",
          }}
        tooltip={{ enable: true }}
        tooltipRender={handleTooltipRender}
        axisLabelRender={handleAxisLabelRender}
        height="420px"
      >
        <Inject services={[LineSeries, Category, Logarithmic, Tooltip, Legend]} />

        <SeriesCollectionDirective>
          {seriesForRender.map(({ series: s, color, lineWidth, lineOpacity }, i) => (
            <SeriesDirective
              key={`${s.name}-${s.channelId ?? i}-${i}`}
              dataSource={s.data || []}
              xName="x"
              yName={useLog ? "activePlot" : yName}
              type="Line"
              name={s.name}
              width={lineWidth}
              opacity={lineOpacity}
              fill={color}
              border={{ color, width: lineWidth }}
              animation={{ enable: true, duration: 500 }}
            />
          ))}
        </SeriesCollectionDirective>
      </ChartComponent>
    </div>
  );
}